import { Suspense, useState } from "react";
import {
  EventInsuranceType,
  EventClaim,
  VehicleInsuranceType,
  VehicleClaimProcessStage,
} from "../../../types/eventClaim.types";
import { Modal } from "flowbite-react";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import { deleteEventClaim } from "../../../common/services/eventCliam.api";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import Loading from "@/common/components/ui/loading/Loading";
import { maskPhone } from "@/common/helpers/mask.helpers";
export type Props = {
  eventClaim?: EventClaim;
  onClose: () => void;
  onDelete: (eventClaimToDelete: EventClaim) => void;
};

const InsuranceClaimSchema = Yup.object().shape({
  eventType: Yup.string().required("Tipo do evento é obrigatório"),
});

export function DeleteEventClaim(props: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab] = useState("resumo");
  const { notifyError } = useToastContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const initialValues = props.eventClaim || {
    _id: undefined,
    eventType: "",
    eventDate: "",
    location: "",
    state: "",
    city: "",
    licensePlate: "",
    associateName: "",
    vehicleType: "",
    phone: "",
    whatsapp: "",
    email: "",
    reportText: "",
    documents: [],
    associatedTrigger: false,
    thirdPartiesInvolved: false,
    thirdPartiesCount: 0,
    thirdParties: [],
    status: VehicleClaimProcessStage.NOTICE,
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const response = await deleteEventClaim(values._id);
      if (!response.ok)
        return notifyError(
          response.message ||
            "Erro ao excluir evento. Por favor, tente novamente."
        );
      props.onDelete(values);
    } catch (error) {
      notifyError("Erro ao excluir evento. Por favor, tente novamente.");
      props.onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async (values) => {
    setShowConfirmModal(false);
    await handleSubmit(values);
  };

  return (
    <Suspense fallback={<Loading />}>
      <Modal
        className="relative text-black"
        show={true}
        onClose={() => props.onClose()}
        size="7xl"
        theme={{
          content: {
            base: "relative h-full w-full p-4 md:h-auto",
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col",
          },
        }}
      >
        <Modal.Header className="text-black">
          <h2 className="text-black">
            {initialValues._id
              ? `Evento: ${
                  initialValues.associateName
                    ? initialValues.associateName
                        .split(" ")
                        .map(
                          (name) =>
                            name.charAt(0).toUpperCase() +
                            name.slice(1).toLowerCase()
                        )
                        .join(" ")
                    : "Desconhecido"
                }`
              : "Novo Evento"}
          </h2>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={InsuranceClaimSchema}
            onSubmit={() => {}}
          >
            {() => (
              <Form>
                <div className="space-y-4 border rounded-lg p-3">
                  {/* Tab Navigation */}
                  <div className="flex mb-4 gap-3">
                    <button
                      type="button"
                      className={`px-4 py-2 ${
                        activeTab === "resumo"
                          ? "bg-[#E44131] text-white rounded-t"
                          : "text-gray-500"
                      }`}
                      disabled
                    >
                      Resumo
                    </button>
                  </div>

                  {/* Tab Content */}
                  {activeTab === "resumo" && (
                    <div className="resumo">
                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <ComboBoxFormik
                            label="Tipo do evento:"
                            name="eventType"
                            className="readonly"
                            disabled={true}
                            options={[
                              { value: "", label: "-" },
                              {
                                value: EventInsuranceType.REVIEW,
                                label: "REVISÃO",
                              },
                              {
                                value: EventInsuranceType.COLLISION,
                                label: "COLISÃO",
                              },
                              {
                                value: EventInsuranceType.THEFT,
                                label: "ROUBO/FURTO",
                              },
                              {
                                value: EventInsuranceType.MOTORCYCLE_RETRIEVAL,
                                label:
                                  "RECOLHIMENTO DA MOTOCICLETA (R$200,00 ATÉ 50KM + R$2,00 POR KM EXCEDENTE)",
                              },
                              {
                                value: EventInsuranceType.WHEEL_OR_TIRE_DAMAGE,
                                label: "RODA AMASSADA / PNEU CORTADO (R$80,00)",
                              },
                              {
                                value: EventInsuranceType.KEY_REPLACEMENT,
                                label:
                                  "SUBSTITUIÇÃO DE CHAVE OU MIOLO (R$300,00)",
                              },
                              {
                                value:
                                  EventInsuranceType.WITHDRAWAL_CANCELLATION,
                                label: "DESISTÊNCIA DE RETIRADA (R$100,00)",
                              },
                              {
                                value:
                                  EventInsuranceType.OUT_OF_COVERAGE_RADIUS,
                                label: "EXCEDER O RAIO DE ATUAÇÃO (R$150,00)",
                              },
                              {
                                value:
                                  EventInsuranceType.MAINTENANCE_WITHOUT_SCHEDULING,
                                label: "MANUTENÇÃO SEM AGENDAMENTO (R$50,00)",
                              },
                              {
                                value: EventInsuranceType.NO_SHOW,
                                label:
                                  "NO SHOW - AUSÊNCIA / ATRASO EM AGENDAMENTO (R$25,00)",
                              },
                              {
                                value: EventInsuranceType.MOTORCYCLE_SEIZURE,
                                label: "APREENSÃO DA MOTOCICLETA (R$700,00)",
                              },
                              {
                                value:
                                  EventInsuranceType.BLOCKED_FOR_NONPAYMENT,
                                label:
                                  "BLOQUEIO POR INADIMPLÊNCIA, MAU USO OU EXCEDER A VELOCIDADE MÁXIMA DE CONDUÇÃO (R$20,00)",
                              },
                              {
                                value: EventInsuranceType.MISCONDUCT_OR_ABUSE,
                                label:
                                  "CONSTATAÇÃO DE MAU USO OU CONDUTA (R$300,00)",
                              },
                            ]}
                          />
                        </div>

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Data e hora do evento:"
                            name="eventDate"
                            type="datetime-local"
                            className="w-full read-only:true"
                            readonly={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Local do evento:"
                            name="location"
                            className="w-full"
                            readonly={true}
                          />
                        </div>

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Estado:"
                            name="state"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Cidade:"
                            name="city"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Placa:"
                            name="licensePlate"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Nome do associado:"
                            name="associateName"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <ComboBoxFormik
                            label="Tipo do veículo:"
                            name="vehicleType"
                            options={[
                              { value: "", label: "-" },
                              {
                                value: VehicleInsuranceType.CAR,
                                label: "Carro",
                              },
                              {
                                value: VehicleInsuranceType.MOTORCYCLE,
                                label: "Moto",
                              },
                              {
                                value: VehicleInsuranceType.TRUCK,
                                label: "Caminhão",
                              },
                            ]}
                            disabled={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Telefone de WhatsApp:"
                            name="whatsapp"
                            type="tel"
                            className="w-full"
                            readonly={true}
                            mask={maskPhone}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Telefone:"
                            name="phone"
                            type="tel"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="E-mail:"
                            name="email"
                            type="email"
                            className="w-full"
                            readonly={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botões do formulário */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <ButtonWithLoader
                      type="button"
                      isLoading={isLoading}
                      disabled={isLoading}
                      onClick={handleDeleteClick}
                    >
                      Excluir
                    </ButtonWithLoader>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
        {isLoading && <Loading />}

        {/* Modal de confirmação */}
        <Modal
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          size="md"
        >
          <Modal.Header>Confirmar exclusão</Modal.Header>
          <Modal.Body>
            <p>Tem certeza que deseja excluir este evento?</p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="bg-red-600 text-white px-4 py-2 rounded mr-2"
              onClick={() => handleConfirmDelete(initialValues)}
            >
              Sim, excluir
            </button>
            <button
              className="bg-gray-200 px-4 py-2 rounded"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </button>
          </Modal.Footer>
        </Modal>
      </Modal>
    </Suspense>
  );
}
